import { proxy, subscribe } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import { resetStore } from '@/lib/valtio/reset-store'

import type { RundownCueOrderItem } from '~shared/rundown/rundown'
import { fastDeepEqual } from '~shared/utils'

import { getRundown, rundownStoreReadonly } from '../data/rundown.store'

import { copyCueOrder } from './cue.order'

type PendingCueOrderState = {
  /**
   * The order the user just dropped, shown until the server's order arrives.
   * Null when there is nothing pending, which is the normal state.
   */
  order: RundownCueOrderItem[] | null

  /**
   * The rundown's cues as they were when the save went through. Null while the
   * save is in flight. A later change to `rundown.cues` is the server's answer
   * and ends the pending order, whether or not it matches.
   */
  cuesAtSave: RundownCueOrderItem[] | null
}

const initialState: PendingCueOrderState = {
  order: null,
  cuesAtSave: null,
}

/**
 * A cue order the user dropped, shown ahead of `rundown.cues`.
 *
 * The rundown store is written by the Firestore listener only, and the
 * listener is async. A drop has to show the new order in the same commit, so
 * it is held here until the listener catches up. Then it is cleared, and the
 * listener's order is what is shown.
 *
 * @remarks
 *   The order is ended in one of three ways:
 *
 *   - the listener delivers the same order: nothing left to show
 *   - the save fails: the drop is rolled back, nothing was saved
 *   - the save succeeds and the rundown's cues change: the server's order wins,
 *     even if it differs (eg. a concurrent change). Otherwise a pending order
 *     that never matches would hide every later change. Snapshots that leave
 *     the cues alone (eg. a rename) do not end it. Kept out of
 *     `cue-dnd.store.ts` on purpose: `cue.derived` reads this store, and
 *     `cue-dnd.store` imports `cue.derived`, so putting it there is a cycle.
 */
const pendingCueOrderStore = proxy<PendingCueOrderState>(
  deepClone(initialState),
)

export const pendingCueOrderActions = {
  /**
   * Show `order` ahead of the rundown's order. Copies it first, because valtio
   * would otherwise turn the caller's arrays into proxies in place.
   */
  set(order: RundownCueOrderItem[]) {
    pendingCueOrderStore.order = structuredClone(order)
    pendingCueOrderStore.cuesAtSave = null
  },

  /**
   * The save of `order` went through. Ends the pending order straight away if
   * the listener has already delivered the same order, otherwise records the
   * cues it is compared against. Snapshots that leave those cues alone (eg. a
   * rename) do not end it.
   *
   * Does nothing if a later drop has replaced `order` in the meantime.
   */
  markSaved(order: RundownCueOrderItem[]) {
    if (!isPending(order)) return

    if (fastDeepEqual(getRundown()?.cues, order)) {
      clear()
      return
    }

    pendingCueOrderStore.cuesAtSave = copyCueOrder(getRundown()?.cues ?? [])
  },

  /**
   * The save of `order` failed: drop it, which rolls the screen back.
   *
   * Does nothing if a later drop has replaced `order` in the meantime, since
   * that drop is still on its way to the server.
   */
  rollback(order: RundownCueOrderItem[]) {
    if (!isPending(order)) return

    clear()
  },

  /** Called when leaving a rundown. */
  resetStore: () => {
    resetStore(pendingCueOrderStore, initialState)
  },
}

function clear() {
  pendingCueOrderStore.order = null
  pendingCueOrderStore.cuesAtSave = null
}

/** Is `order` the pending one? Compared by content, not by reference. */
function isPending(order: RundownCueOrderItem[]) {
  const pending = pendingCueOrderStore.order
  return pending !== null && fastDeepEqual(pending, order)
}

/**
 * End the pending order when the listener delivers the server's order.
 */
subscribe(rundownStoreReadonly, () => {
  const { order, cuesAtSave } = pendingCueOrderStore
  if (!order) return

  const cues = getRundown()?.cues
  const answered =
    cuesAtSave !== null
      ? !fastDeepEqual(cues, cuesAtSave)
      : fastDeepEqual(cues, order)

  if (answered) clear()
})

/**
 * The store itself, for the derived layer.
 *
 * Read-only by type, so writes have to go through the actions above.
 */
export const pendingCueOrderStoreReadonly: Readonly<PendingCueOrderState> =
  pendingCueOrderStore

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(pendingCueOrderStore, {
    name: 'pendingCueOrderStore',
    enabled: true,
  })
}
// #endregion
